import HStack from "@components/layout/HStack";
import VStack from "@components/layout/VStack";
import React, { useEffect, useMemo, useState } from "react";

import { CloseIcon, GroupFilledIcon, PersonIcon } from "@/pages/taskpane/components/icons/IconCustom";
import { useOutlookActions } from "@/pages/taskpane/lib/office/hooks";
import Button from "@components/ui/Button";
import Status from "@components/ui/Status";
import { useAppointmentStore, useAttendeesStore, useOutlookStore, useThemeStore } from "@shared/stores";
import { useTranslation } from "react-i18next";
import { AttendeeInfo } from "../../resources/services/types/attendeeEntity";
import { EStatusResource } from "../../resources/services/types/enum";
import { ResourceEntity } from "../../resources/services/types/resourceEntity";
import { isAllDayMeeting, parseISOString } from "../../resources/ulti";
import { useTimelineAttendee } from "../hooks/useTimelineAttendee";
import Timeline from "./Timeline";
import LoadingOverLay from "@/pages/taskpane/components/ui/LoadingOverLay";

const ModalShowAttendees = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useThemeStore();
  const [data, setData] = useState<AttendeeInfo[]>([]);
  const [internalAttendees, setInternalAttendees] = useState<AttendeeInfo[]>([]);
  const [externalAttendees, setExternalAttendees] = useState<AttendeeInfo[]>([]);
  const [currentSort, setCurrentSort] = useState<"total" | EStatusResource>("total");
  const { RemoveResources } = useOutlookActions();
  const { startDateTimeZone, endDateTimeZone } = useAppointmentStore();
  const { recurringDates } = useOutlookStore();

  const isAllDayEvent = useMemo(
    () => isAllDayMeeting(parseISOString(startDateTimeZone), parseISOString(endDateTimeZone)),
    [startDateTimeZone, endDateTimeZone]
  );

  const { mainAttendee, requiredAttendees, optionalAttendees, removeRequiredAttendee, removeOptionalAttendee, isLoading: isLoadingAttendees } =
    useAttendeesStore();

  const isRecurring = useMemo(() => recurringDates.length > 0, [recurringDates]);

  useEffect(() => {
    const attendees = [
      ...(mainAttendee ? [mainAttendee] : []),
      ...(requiredAttendees || []),
      ...(optionalAttendees || []),
    ];
    setData(attendees);
    setInternalAttendees(
      attendees.filter(
        (attendee) => attendee.status(startDateTimeZone, endDateTimeZone, isRecurring, isAllDayEvent) !== EStatusResource.Disable
      )
    );
    setExternalAttendees(
      attendees.filter(
        (attendee) => attendee.status(startDateTimeZone, endDateTimeZone, isRecurring, isAllDayEvent) === EStatusResource.Disable
      )
    );
    setCurrentSort("total");
  }, [requiredAttendees, optionalAttendees, mainAttendee, startDateTimeZone, endDateTimeZone, isRecurring, isAllDayEvent]);

  const handleSortAttendees = (status: "total" | EStatusResource) => {
    let internal = [] as AttendeeInfo[];
    let external = [] as AttendeeInfo[];
    if (status === "total") {
      internal = data.filter(
        (attendee) => attendee.status(startDateTimeZone, endDateTimeZone, isRecurring, isAllDayEvent) !== EStatusResource.Disable
      );
      external = data.filter(
        (attendee) => attendee.status(startDateTimeZone, endDateTimeZone, isRecurring, isAllDayEvent) === EStatusResource.Disable
      );
    } else if (status === EStatusResource.Available) {
      internal = data.filter(
        (attendee) => attendee.status(startDateTimeZone, endDateTimeZone, isRecurring, isAllDayEvent) === EStatusResource.Available
      );
      external = [];
    } else if (status === EStatusResource.Unavailable) {
      internal = data.filter(
        (attendee) => attendee.status(startDateTimeZone, endDateTimeZone, isRecurring, isAllDayEvent) === EStatusResource.Unavailable
      );
      external = [];
    } else if (status === EStatusResource.Disable) {
      internal = [];
      external = data.filter(
        (attendee) => attendee.status(startDateTimeZone, endDateTimeZone, isRecurring, isAllDayEvent) === EStatusResource.Disable
      );
    }
    setInternalAttendees(internal);
    setExternalAttendees(external);
    setCurrentSort(status);
  };

  const handleDeleteAttendee = (attendee: AttendeeInfo) => {
    if (attendee.isRequired) {
      removeRequiredAttendee(attendee.email);
    } else {
      removeOptionalAttendee(attendee.email);
    }
    RemoveResources([new ResourceEntity({ email: attendee.email, name: attendee.name })]);
  };

  const listSort = useMemo(() => {
    const internalAvailable = data.filter(
      (attendee) => attendee.status(startDateTimeZone, endDateTimeZone, isRecurring, isAllDayEvent) === EStatusResource.Available
    ).length;
    const internalUnavailable = data.filter(
      (attendee) => attendee.status(startDateTimeZone, endDateTimeZone, isRecurring, isAllDayEvent) === EStatusResource.Unavailable
    ).length;
    return [
      { label: t("Total"), key: "total", value: data.length },
      internalAttendees.length && internalAvailable
        ? {
            label: t("Available"),
            key: EStatusResource.Available,
            value: internalAvailable,
          }
        : null,
      internalAttendees.length && internalUnavailable
        ? {
            label: t("Busy"),
            key: EStatusResource.Unavailable,
            value: internalUnavailable,
          }
        : null,
      externalAttendees.length > 0
        ? {
            label: t("External"),
            key: EStatusResource.Disable,
            value: data.filter(
              (attendee) => attendee.status(startDateTimeZone, endDateTimeZone, isRecurring, isAllDayEvent) === EStatusResource.Disable
            ).length,
          }
        : null,
    ].filter((item) => item !== null);
  }, [data, t]);

  // Tính toán visibility và class names
  const showInternal =
    internalAttendees.length > 0 && (currentSort === "total" || currentSort !== EStatusResource.Disable);
  const showExternal =
    externalAttendees.length > 0 && (currentSort === "total" || currentSort === EStatusResource.Disable);
  const bothVisible = showInternal && showExternal;
  // Dùng flex để tự động phân chia: 55% và 45% khi cả hai hiển thị, flex-1 khi chỉ một
  const internalClassName = `w-full min-h-0 ${bothVisible && currentSort === "total" ? "flex-[0.65]" : "flex-1"}`;
  const externalClassName = `w-full min-h-0 ${bothVisible && currentSort === "total" ? "flex-[0.35]" : "flex-1"}`;

  return (
    <VStack className="w-full h-full px-5 pt-4 pb-6 flex flex-col" gap={4}>
      {isLoadingAttendees ? (
        <LoadingOverLay />
      ) : null}
      <HStack
        justifyContent="between"
        className="w-full text-sm bg-extraLightGray rounded overflow-hidden dark:bg-deepGray"
        gap={1}
      >
        {listSort.map((i) => {
          return (
            <div
              key={i.key}
              className={`flex flex-col items-center justify-center flex-1 px-3 py-2 cursor-pointer rounded hover:bg-softBlue dark:hover:bg-deepPrimaryBlue  ${
                i.key === currentSort ? "bg-softBlue dark:bg-deepPrimaryBlue" : ""
              }`}
              onClick={() => handleSortAttendees(i.key as "total" | EStatusResource)}
            >
              {i.key === "total" ? (
                <HStack gap={1}>
                  <GroupFilledIcon isDarkMode={isDarkMode} size={20} />
                  <span>{i.value}</span>
                </HStack>
              ) : (
                <HStack gap={1}>
                  <Status status={i.key as EStatusResource} className="w-5 h-5" />
                  <span>{i.value}</span>
                </HStack>
              )}

              <p>{i.label}</p>
            </div>
          );
        })}
      </HStack>

      {/* Internal Attendees */}
      {showInternal && (
        <VStack gap={3} className={internalClassName}>
          <p className="font-semibold">{t("Internal")}</p>

          <VStack className="w-full flex-1 overflow-y-auto min-h-0" gap={3}>
            {internalAttendees.map((attendee) => (
              <InternalAttendees
                key={attendee.email}
                attendee={attendee}
                onAction={handleDeleteAttendee}
                isMainAttendee={attendee.email === mainAttendee?.email}
                isRecurring={isRecurring}
                isAllDayEvent={isAllDayEvent}
              />
            ))}
          </VStack>
        </VStack>
      )}

      {/* External Attendees */}
      {showExternal && (
        <VStack className={externalClassName}>
          <p className="font-semibold mb-3">{t("External")}</p>

          <VStack className="w-full flex-1 overflow-y-auto min-h-0" gap={3}>
            {externalAttendees.map((attendee) => (
              <ExternalAttendees
                key={attendee.email}
                attendee={attendee}
                onAction={handleDeleteAttendee}
                isMainAttendee={attendee.email === mainAttendee?.email}
              />
            ))}
          </VStack>
        </VStack>
      )}
    </VStack>
  );
};

export default ModalShowAttendees;

const InternalAttendees = ({
  attendee,
  onAction,
  isMainAttendee,
  isRecurring,
  isAllDayEvent,
}: {
  attendee: AttendeeInfo;
  onAction: (attendee: AttendeeInfo) => void;
  isMainAttendee?: boolean;
  isRecurring: boolean;
  isAllDayEvent: boolean;
}) => {
  const { isDarkMode } = useThemeStore();
  const { startDateTimeZone, endDateTimeZone, startDate, endDate, timeZone } = useAppointmentStore();
  const { getAttendeeStatusByDate } = useTimelineAttendee();

  const handleDateChange = (newDate: string) => {
    getAttendeeStatusByDate(attendee, newDate, timeZone);
  };

  const openHours = useMemo((): { startTime: string; endTime: string } => {
    if (attendee.isHoliday) {
      return { startTime: "2025-12-05T00:00:00", endTime: "2025-12-05T00:00:00" };
    } else if (attendee.workingHours) {
      return {
        startTime: `2025-12-05T${attendee.workingHours.startTime}:00`,
        endTime: `2025-12-05T${attendee.workingHours.endTime}:00`,
      };
    }
    return { startTime: "", endTime: "" };
  }, [attendee]);

  return (
    <div className="w-full p-3 border border-lightGray rounded-lg text-sm">
      <HStack justifyContent="between" className="mb-0">
        <HStack gap={2}>
          <Status status={attendee.status(startDateTimeZone, endDateTimeZone, isRecurring, isAllDayEvent)} className="w-4 h-4 mr-1" />
          <p>{attendee.name}</p>
        </HStack>
        {isMainAttendee ? null : (
          <Button leftIcon={<CloseIcon isDarkMode={isDarkMode} size={16} />} onClick={() => onAction(attendee)} />
        )}
      </HStack>
      {isRecurring ? (
        <HStack justifyContent="end" className="w-full">
           <p className="text-xs mt-3">
              Availability: {attendee.noOfAvailable}/{attendee.total}
            </p>
        </HStack>
      ) : (
        <Timeline
          selectedStartTime={startDateTimeZone || (startDate ? startDate.toISOString() : "")}
          selectedEndTime={endDateTimeZone || (endDate ? endDate.toISOString() : "")}
          meetings={attendee.meetings.map((meeting) => {
            return {
              id: meeting.id,
              title: "",
              startTime: meeting.startTime,
              endTime: meeting.endTime,
              status: meeting.busyStatus,
            };
          })}
          currentDate={startDateTimeZone}
          attendeeStatus={attendee.status(startDateTimeZone, endDateTimeZone, isRecurring, isAllDayEvent)}
          onDateChange={handleDateChange}
          openStartTime={openHours.startTime}
          openEndTime={openHours.endTime}
        />
      )}
    </div>
  );
};

const ExternalAttendees = ({
  attendee,
  onAction,
  isMainAttendee,
}: {
  attendee: AttendeeInfo;
  onAction: (attendee: AttendeeInfo) => void;
  isMainAttendee?: boolean;
}) => {
  const { isDarkMode } = useThemeStore();
  return (
    <div className="w-full p-2 bg-extraLightGray rounded text-sm dark:bg-deepGray">
      <HStack justifyContent="between" className="">
        <HStack gap={2}>
          <HStack justifyContent="center" className="w-5 h-5 rounded-full bg-lightGray">
            <PersonIcon isDarkMode={isDarkMode} size={16} />
          </HStack>
          <p>{attendee.name}</p>
        </HStack>
        {isMainAttendee ? null : (
          <Button leftIcon={<CloseIcon isDarkMode={isDarkMode} size={16} />} onClick={() => onAction(attendee)} />
        )}
      </HStack>
    </div>
  );
};
